import { useState } from "react";
import { Bell, BellRinging, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  enablePushForTruck,
  disablePushForTruck,
  isIosLike,
  isStandalone,
  isPushSupported,
  permissionState,
} from "../lib/push";
import { trackSiteEvent } from "../lib/analytics";
import { Capacitor } from "@capacitor/core";
import IosInstallSheet from "./IosInstallSheet";

/**
 * NotifyMeBell — one-tap "ping me when this truck goes live".
 *
 * Hides the complexity of permission, subscription, and follow-row
 * creation behind a single bell button. Designed for both the truck
 * detail sheet and (compact mode) inline truck cards.
 *
 * Props:
 *   truckId         — required.
 *   truckName       — used for friendly toast/iOS sheet copy.
 *   subscribed      — current state from parent (parent owns it so
 *                     multiple bells across the page stay in sync).
 *   onChange(next)  — fire after a successful flip; parent updates state.
 *   compact         — small inline variant (used on list cards).
 *   className       — passthrough for layout.
 */
export default function NotifyMeBell({
  truckId,
  truckName = null,
  subscribed = false,
  onChange,
  compact = false,
  className = "",
}) {
  const [busy, setBusy] = useState(false);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);

  const handleClick = async () => {
    if (busy || !truckId) return;

    if (subscribed) {
      // Unsubscribe path is fast and never needs permission.
      setBusy(true);
      try {
        await disablePushForTruck(truckId);
        trackSiteEvent("notif_truck_unsubscribed");
        onChange?.(false);
        toast.success(truckName ? `You won't get pings for ${truckName}` : "Notifications off for this truck");
      } finally {
        setBusy(false);
      }
      return;
    }

    // Native app — skip every web-push check; permission + follow row are
    // handled inside enablePushForTruck's native branch.
    if (Capacitor.isNativePlatform()) {
      setBusy(true);
      const res = await enablePushForTruck(truckId);
      setBusy(false);
      if (res.ok) {
        trackSiteEvent("notif_truck_subscribed");
        onChange?.(true);
        toast.success(
          truckName
            ? `You'll get a ping when ${truckName} is live`
            : "We'll ping you the next time this truck is live"
        );
      } else if (res.reason === "denied") {
        trackSiteEvent("notif_truck_blocked");
        toast.error("Notifications are off for TruckTrackr. Turn them on in Settings → TruckTrackr → Notifications.");
      } else {
        toast.error("Couldn't turn that on — try again.");
      }
      return;
    }

    // iOS Safari (non-PWA) — show install coachmark instead of failing.
    if (isIosLike() && !isStandalone()) {
      trackSiteEvent("notif_truck_ios_install_hint");
      setIosSheetOpen(true);
      return;
    }

    // Browsers without push support — be honest.
    if (!isPushSupported()) {
      toast.error("This browser can't receive push notifications.");
      return;
    }

    // Permission already denied — guide them to settings.
    if (permissionState() === "denied") {
      trackSiteEvent("notif_truck_blocked");
      toast.error("Notifications blocked. Re-enable in your browser's site settings.");
      return;
    }

    setBusy(true);
    const res = await enablePushForTruck(truckId);
    setBusy(false);
    if (res.ok) {
      trackSiteEvent("notif_truck_subscribed");
      onChange?.(true);
      toast.success(
        truckName
          ? `You'll get a ping when ${truckName} is live`
          : "We'll ping you the next time this truck is live"
      );
    } else if (res.reason === "denied" || res.reason === "default") {
      trackSiteEvent("notif_truck_blocked");
      toast.error("Notifications blocked. You can re-enable in browser settings.");
    } else if (res.reason === "ios_needs_pwa") {
      setIosSheetOpen(true);
    } else {
      toast.error("Couldn't enable notifications. Try again.");
    }
  };

  // Compact (inline card) variant — pill button, minimal label.
  if (compact) {
    return (
      <>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleClick(); }}
          disabled={busy}
          aria-pressed={subscribed}
          aria-label={subscribed ? "Notifications on for this truck" : "Notify me when this truck is live"}
          className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold border transition ${
            subscribed
              ? "bg-primary/15 text-primary border-primary/30 hover:bg-primary/20"
              : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-primary/40 hover:bg-primary/5"
          } disabled:opacity-50 ${className}`}
          data-testid={`notify-bell-${truckId}`}
          data-state={subscribed ? "on" : "off"}
        >
          {subscribed
            ? <BellRinging weight="fill" className="h-3.5 w-3.5" />
            : <Bell weight="duotone" className="h-3.5 w-3.5" />}
          <span>{subscribed ? "On" : "Notify me"}</span>
        </button>
        <IosInstallSheet
          open={iosSheetOpen}
          truckName={truckName}
          onClose={() => setIosSheetOpen(false)}
        />
      </>
    );
  }

  // Full variant — used in detail sheet header, larger and label-forward.
  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-pressed={subscribed}
        className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-xs font-bold border transition ${
          subscribed
            ? "bg-primary/15 text-primary border-primary/40 hover:bg-primary/20"
            : "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
        } disabled:opacity-50 ${className}`}
        data-testid={`notify-bell-${truckId}`}
        data-state={subscribed ? "on" : "off"}
      >
        {subscribed
          ? <BellRinging weight="fill" className="h-4 w-4" />
          : <Lightning weight="duotone" className="h-4 w-4" />}
        <span>{subscribed ? "Notifications on" : "Notify me when live"}</span>
      </button>
      <IosInstallSheet
        open={iosSheetOpen}
        truckName={truckName}
        onClose={() => setIosSheetOpen(false)}
      />
    </>
  );
}
